/**
 * Encryption Utility
 * Provides encryption/decryption functions for sensitive data
 * Uses AES encryption with a secret key from environment variables
 */

import CryptoJS from 'crypto-js';
import { reportError } from '../../sentry';

/**
 * Get the encryption key from environment variables
 * @returns {string} Encryption key
 */
const getEncryptionKey = () => {
  const key = process.env.REACT_APP_ENCRYPTION_KEY;

  if (!key) {
    throw new Error('Encryption key is not configured. Please set REACT_APP_ENCRYPTION_KEY.');
  }

  return key;
};

/**
 * Encrypts a string value using AES encryption
 * @param {string} value - The value to encrypt
 * @returns {string} Encrypted string (base64 encoded)
 */
export const encryptValue = (value) => {
  if (!value || typeof value !== 'string') {
    return value;
  }

  try {
    const key = getEncryptionKey();
    const encrypted = CryptoJS.AES.encrypt(value, key).toString();
    return encrypted;
  } catch (error) {
    reportError(error, 'encrypt value');
    throw new Error('Failed to encrypt value');
  }
};

/**
 * Decrypts an encrypted string value using AES decryption
 * Note: This is primarily for frontend testing/debugging
 * Backend should handle decryption
 * @param {string} encryptedValue - The encrypted value to decrypt
 * @returns {string} Decrypted string
 */
export const decryptValue = (encryptedValue) => {
  if (!encryptedValue || typeof encryptedValue !== 'string') {
    return encryptedValue;
  }

  try {
    const key = getEncryptionKey();
    const decrypted = CryptoJS.AES.decrypt(encryptedValue, key);
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    return decryptedString;
  } catch (error) {
    reportError(error, 'decrypt value');
    throw new Error('Failed to decrypt value');
  }
};
